import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { clearInvite, saveInvite } from "@/lib/invite";
import { NamtechFooter } from "@/components/poc/NamtechFooter";
import { GoogleButton } from "@/components/auth/GoogleButton";

const USERNAME_RE = /^[a-z0-9._-]{3,30}$/;

const Field = ({ label, hint, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-mono uppercase tracking-widest c-muted">{label}</span>
    {children}
    {hint && <span className="block text-[11px] c-muted">{hint}</span>}
  </label>
);

const validate = (f) => {
  if (f.name.trim().length < 2) return "Escribe tu nombre público";
  if (!USERNAME_RE.test(f.username)) return "El usuario debe tener de 3 a 30 caracteres: minúsculas, números, punto, guion o guion bajo.";
  if (f.password.length < 8) return "La contraseña debe tener al menos 8 caracteres";
  if (f.password !== f.confirm) return "Las contraseñas no coinciden";
  return null;
};

export default function InviteSignupPage() {
  const { codigo } = useParams();
  const [sp] = useSearchParams();
  const inv = sp.get("inv");
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", username: "", password: "", confirm: "", ciudad: "", telefono: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { saveInvite(codigo); }, [codigo]);
  if (user) return <Navigate to="/conecta" replace />;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: k === "username" ? e.target.value.toLowerCase().trim() : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    if (v) return setError(v);
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", { name: f.name.trim(), username: f.username, password: f.password, codigo, inv,
        ciudad: f.ciudad.trim() || null, telefono: f.telefono.trim() || null });
      clearInvite();
      setUser(data);
      nav("/conecta", { replace: true });
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="invite-signup-page" className="min-h-screen max-w-md mx-auto px-6 safe-top flex flex-col">
      <div className="flex-1 flex flex-col justify-center gap-5 py-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest c-accent">Invitación {codigo}</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Únete a CONECTA</h1>
          <p className="c-text-2 mt-1 text-sm">Tu cuenta es tuya y empieza vacía. Quedarás conectado con quien te invitó.</p>
        </div>
        <GoogleButton testid="invite-google-btn" label="Registrarme con Google" mode="invite" returnPath={`/i/${codigo}/registro`} />
        <div className="flex items-center gap-3 text-xs c-muted"><span className="flex-1 border-t c-line" />o con usuario<span className="flex-1 border-t c-line" /></div>
        <form onSubmit={submit} noValidate className="space-y-3.5" data-testid="invite-signup-form">
          <Field label="Nombre público" hint="Así te verá tu red."><input data-testid="invite-name-input" className="c-input" autoComplete="name" value={f.name} onChange={set("name")} /></Field>
          <Field label="Usuario" hint="3 a 30 caracteres: minúsculas, números, punto o guion."><input data-testid="invite-username-input" className="c-input" autoCapitalize="none" autoCorrect="off" autoComplete="username" value={f.username} onChange={set("username")} /></Field>
          <Field label="Contraseña (mín. 8)"><input data-testid="invite-password-input" className="c-input" type="password" autoComplete="new-password" value={f.password} onChange={set("password")} /></Field>
          <Field label="Confirmar contraseña"><input data-testid="invite-confirm-input" className="c-input" type="password" autoComplete="new-password" value={f.confirm} onChange={set("confirm")} /></Field>
          <Field label="Ciudad"><input data-testid="invite-city-input" className="c-input" value={f.ciudad} onChange={set("ciudad")} placeholder="Ciudad de México" /></Field>
          <Field label="Teléfono (opcional)" hint="Solo guardamos una huella cifrada para que tus conocidos te encuentren."><input data-testid="invite-phone-input" className="c-input" type="tel" inputMode="tel" value={f.telefono} onChange={set("telefono")} placeholder="55 1234 5678" /></Field>
          {error && <p data-testid="invite-signup-error" className="text-sm text-red-500">{error}</p>}
          <button data-testid="invite-signup-submit" disabled={busy} className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2 text-base">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Crear mi cuenta</button>
        </form>
        <p className="text-sm text-center c-text-2">¿Ya tienes cuenta? <Link to="/login" data-testid="invite-signup-login-link" className="c-accent font-semibold">Entrar</Link></p>
      </div>
      <NamtechFooter />
    </div>
  );
}
